import React, { memo } from 'react';
import { Table, Tooltip } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TablePaginationConfig } from 'antd/es/table/interface';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { formatCreationDate as formatDate } from 'Common/utils';
import Loading from 'Common/Components/Loading';
import { User } from 'utils/apiClient/services/contacts/types';
import { Product } from 'utils/apiClient/services/inventory/types';
import { useProductDetails } from 'Inventory/hooks/products';
import NoStock from 'assets/images/icons/Type=NoStock.svg';
import BelowSafety from 'assets/images/icons/Type=BelowSafety.svg';
import BelowReorder from 'assets/images/icons/Type=BelowReorder.svg';
import OnStock from 'assets/images/icons/Type=OnStock.svg';
import {
  InventoryRecord,
  InventoryReportTableParams,
} from 'utils/apiClient/services/reports/types';

export interface Props {
  loading: boolean;
  data: Array<InventoryRecord> | undefined;
  handleTableChange: (t: InventoryReportTableParams) => void;
  tableParams: InventoryReportTableParams;
  users: Array<User> | undefined;
  products: Array<Product> | undefined;
}

const CustomTag = styled.div`
  box-sizing: border-box;
  margin: 2px;
  padding: 1px 8px;
  color: rgba(0, 0, 0, 0.65);
  background: rgba(0, 0, 0, 0.04);
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  font-variant: tabular-nums;
  line-height: 1.5715;
  list-style: none;
  font-featuresettings: tnum;
  display: inline-block;
  height: auto;
  margin-right: 8px;
  background-color: #fafafa;
  border: 0.5px solid #d9d9d9;
  border-radius: 2px;
  opacity: 1;
  transition: all 0.3s;
`;

const InventoryReportTable: React.FC<Props> = ({
  loading,
  users,
  products,
  data,
  handleTableChange,
  tableParams,
}) => {
  const queryClient = useQueryClient();
  const [account] = useLocalStorageState<AccountPersistInfo>('account');

  const {
    data: productdata,
    isLoading,
    isRefetching,
    refetch: getProduct,
  } = useProductDetails();

  const handleProductDetails = (id: number) => {
    queryClient.setQueryData(['inventory', 'hover-product'], id);
    getProduct();
  };
  return (
    <Table<InventoryRecord>
      css={`
        .ant-table-thead > tr {
          height: 45px;
        }
        .ant-table-placeholder {
          min-height: calc(100vh - 300px);
        }
      `}
      loading={loading}
      dataSource={data}
      size="small"
      pagination={tableParams.pagination as TablePaginationConfig}
      onChange={(pagination, filters, sorter) =>
        handleTableChange({ pagination, filters, sorter })
      }
      scroll={{ x: true }}
    >
      <Table.Column<InventoryRecord>
        key="date"
        width="10%"
        title={<FormattedMessage defaultMessage="Time" />}
        sorter
        defaultSortOrder="descend"
        sortDirections={['descend', 'ascend', 'descend']}
        render={(t) => formatDate(t.date)}
      />
      <Table.Column<InventoryRecord>
        ellipsis
        key="product_name"
        width="20%"
        title={<FormattedMessage defaultMessage="Product" />}
        sorter
        filterMultiple={false}
        filters={
          products !== undefined
            ? [...products].map((p: Pick<Product, 'name' | 'id'>) => ({
                text: p.name,
                value: p.id,
              }))
            : []
        }
        render={(r: InventoryRecord) => {
          const fetchStock = () => {
            let stock = null;
            if (
              productdata !== undefined &&
              productdata.reorder_level !== null &&
              productdata.reorder_level !== undefined &&
              productdata.safety_stock !== null
            ) {
              if (parseFloat(r.quantity) <= 0) {
                stock = <NoStock css="color: #54b25c; margin-bottom: -2px;" />;
              } else if (
                parseFloat(r.quantity) >= 0 &&
                parseFloat(r.quantity) >=
                  parseFloat(productdata?.reorder_level) &&
                parseFloat(r.quantity) >= parseFloat(productdata.safety_stock)
              ) {
                stock = <OnStock css="color: #54b25c; margin-bottom: -2px;" />;
              } else if (
                parseFloat(r.quantity) >= 0 &&
                parseFloat(r.quantity) < parseFloat(productdata.safety_stock)
              ) {
                stock = (
                  <BelowSafety css="color: #54b25c; margin-bottom: -2px;" />
                );
              } else if (
                parseFloat(r.quantity) >= 0 &&
                parseFloat(r.quantity) < parseFloat(productdata.reorder_level)
              ) {
                stock = (
                  <BelowReorder css="color: #54b25c; margin-bottom: -2px;" />
                );
              }
            }
            return stock;
          };
          const getContent = () => {
            if (isLoading || isRefetching)
              return <Loading noText verticallyCentered />;
            if (productdata !== undefined)
              return (
                <div style={{ padding: 10, color: 'black' }}>
                  <div css={{ marginLeft: '2px' }}>{productdata.name}</div>
                  {r.categories.map((n) => (
                    <CustomTag key={n}>{n}</CustomTag>
                  ))}
                  <div style={{ display: 'flex', marginTop: 5 }}>
                    <div style={{ marginRight: 'auto' }}>
                      <div>
                        {productdata.gross_price}
                        {account?.country === 'CH' ? 'CHF' : '€'}
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto', paddingLeft: '5px' }}>
                      <div>
                        <span style={{ paddingTop: '-5px' }}>
                          {parseFloat(productdata.quantity!)}{' '}
                          <FormattedMessage defaultMessage="in stock" />
                        </span>
                        <span
                          style={{
                            paddingLeft: '2px',
                            paddingBottom: '-5px',
                          }}
                        >
                          {fetchStock()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            return null;
          };
          if (products !== undefined) {
            return (
              <Tooltip
                title={() => getContent()}
                placement="right"
                onVisibleChange={() => handleProductDetails(r.product_id)}
                color="white"
                overlayStyle={{ minWidth: '280px' }}
              >
                <span>{r.product_name}</span>
              </Tooltip>
            );
          }
          if (isRefetching) {
            return <Loading noText />;
          }
          return r.product_name;
        }}
      />
      <Table.Column<InventoryRecord>
        width="10%"
        key="reason"
        sorter
        sortDirections={['descend', 'ascend', 'descend']}
        title={<FormattedMessage defaultMessage="Action" />}
        render={(r: InventoryRecord) => {
          if (r.change_event !== null && r.change_type !== null) {
            return `${r.change_type} - ${r.change_event}`;
          }
          return r.reason;
        }}
      />
      <Table.Column<InventoryRecord>
        key="change_source"
        width="10%"
        align="center"
        title={<FormattedMessage defaultMessage="Channel" />}
        sorter
        render={(r: InventoryRecord) => {
          if (!r.change_source) {
            return '-';
          }
          return r.change_source;
        }}
        filters={[
          {
            value: 'ios',
            text: <FormattedMessage defaultMessage="iOS" />,
          },
          {
            value: 'web',
            text: <FormattedMessage defaultMessage="Web" />,
          },
          {
            value: 'calendar',
            text: <FormattedMessage defaultMessage="Calendar" />,
          },
          {
            value: 'other',
            text: <FormattedMessage defaultMessage="Other" />,
          },
        ]}
      />
      <Table.Column<InventoryRecord>
        key="quantity"
        width="10%"
        sorter
        sortDirections={['descend', 'ascend', 'descend']}
        title={<FormattedMessage defaultMessage="Change" />}
        dataIndex="quantity"
      />
      <Table.Column<InventoryRecord>
        key="user"
        width="10%"
        title={<FormattedMessage defaultMessage="User" />}
        dataIndex="user"
        filterMultiple={false}
        sorter
        filters={
          users !== undefined
            ? users.map((user) => ({
                text: user.email,
                value: user.id,
              }))
            : []
        }
      />
    </Table>
  );
};
export default memo(InventoryReportTable);
